/**
 * Sensor type names (SensorTypes_* typeName) the portal offers and supports.
 * Database spellings vary (Omni-30xx / Omni30XX), so names are compared ignoring case,
 * dashes and spaces.
 */
export const OMNI_22XX = 'Omni-22xx';
export const OMNI_30XX = 'Omni-30xx';
export const OMNI_SENSOR_TYPE_NAMES = [OMNI_22XX, OMNI_30XX];

export const normalizeTypeName = (typeName?: string): string =>
   (typeof typeName === 'string' ? typeName : '').toLowerCase().replace(/[^a-z0-9]/g, '');

const isTypeName = (typeName: string, name: string): boolean =>
   normalizeTypeName(typeName) === normalizeTypeName(name);

export const isOmni22xxTypeName = (typeName: string): boolean => isTypeName(typeName, OMNI_22XX);
export const isOmni30xxTypeName = (typeName: string): boolean => isTypeName(typeName, OMNI_30XX);

export const isOmniSensorTypeName = (typeName: string): boolean =>
   isOmni22xxTypeName(typeName) || isOmni30xxTypeName(typeName);

/**
 * The SensorTypes_* record that drives a sensor's calibration modal and alert template.
 * Omni-30xx sensors use their own record; every other sensor keeps the pre-30xx behaviour
 * of using the first Omni-22xx record.
 */
export const findOmniSensorType = (sensorTypes: any[], sensorTypeId?: string) => {
   const ownType = sensorTypes.find(type => type['key'] === sensorTypeId);
   if (ownType && isOmni30xxTypeName(ownType['typeName'])) {
      return ownType;
   }
   // Exact name first, so a differently spelled duplicate never displaces the record used so far.
   return (
      sensorTypes.find(type => type['typeName'] === OMNI_22XX) ||
      sensorTypes.find(type => isOmni22xxTypeName(type['typeName']))
   );
};

/**
 * Config modal type (Current_Modal_Type) for a sensor type name. Only Omni-30xx maps to 3;
 * everything else, including legacy, unknown and missing names, keeps the 2 these call sites
 * always wrote.
 */
export const OMNI_22XX_MODAL_TYPE = 2;
export const OMNI_30XX_MODAL_TYPE = 3;
export const modalTypeForTypeName = (typeName?: string): number =>
   isOmni30xxTypeName(typeName) ? OMNI_30XX_MODAL_TYPE : OMNI_22XX_MODAL_TYPE;

/** Modal types that share the Omni-2100 layout (valve/pump table, step category). */
export const isOmni2100FamilyModalType = (modalType: number): boolean =>
   modalType === OMNI_22XX_MODAL_TYPE || modalType === OMNI_30XX_MODAL_TYPE;
